/**
 * JotAuditPage Component
 *
 * Main page for the JotAudit dashboard with tab navigation
 * Tabs: Overview | Providers | Notes | Rules
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserRoles } from '@features/user';
import {
  AutoAwesome as AIIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { hasRole } from '@utils/roleUtils';

import { EncounterAuditsList } from './components/encounters/EncounterAuditsList';
import { JotAuditPageLayout } from './components/layout';
import { ProvidersList } from './components/providers/ProvidersList';
import { RulesTabContent } from './components/rules/RulesTabContent';
import { TAB_CONFIG } from './constants';
import type { JotAuditTabType } from './types';

const DEFAULT_TAB: JotAuditTabType = 'encounters';


interface TabPanelProps {
  children?: React.ReactNode;
  value: JotAuditTabType;
  activeTab: JotAuditTabType;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, activeTab }) => {
  if (value !== activeTab) {
    return null;
  }

  return <Box sx={{ pt: 3 }}>{children}</Box>;
};

export const JotAuditPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userRoles = useUserRoles();
  const { jotauditDashboard } = useEventTracking();

  const isPracticeAdmin = hasRole(userRoles, 'practice_administrator');

  // Get current tab from URL or default
  const currentTab = useMemo<JotAuditTabType>(() => {
    const tab = searchParams.get('tab');
    if (tab && ['encounters', 'providers', 'notes', 'rules', 'claims'].includes(tab)) {
      return tab as JotAuditTabType;
    }
    return DEFAULT_TAB;
  }, [searchParams]);


  // Track page viewed on mount
  const pageViewedRef = useRef(false);
  useEffect(() => {
    if (isPracticeAdmin && !pageViewedRef.current) {
      pageViewedRef.current = true;
      jotauditDashboard.pageViewed({
        current_tab: currentTab,
      });
    }
  }, [isPracticeAdmin, currentTab, jotauditDashboard]);


  // Handle tab change
  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: JotAuditTabType) => {
      // Track tab change
      jotauditDashboard.tabChanged({
        previous_tab: currentTab,
        new_tab: newValue,
      });
      setSearchParams({ tab: newValue });
    },
    [setSearchParams, currentTab, jotauditDashboard],
  );


  // Handle AI Editor navigation
  const handleOpenAiEditor = useCallback(() => {
    navigate('/rule-build');
  }, [navigate]);

  // Restrict access to practice administrators
  if (!isPracticeAdmin) {
    return (
      <JotAuditPageLayout maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <LockIcon sx={{ fontSize: 48, color: theme.palette.warning.main, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Access restricted
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
            Only practice administrators can view the Audit Center right now.
          </Typography>
        </Paper>
      </JotAuditPageLayout>
    );
  }

  return (
    <JotAuditPageLayout>
      {/* Page Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 0.5,
            }}
          >
            Audit Center
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
            Track performance, billing efficiency, and opportunities for improvement
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* AI Rule Editor Button - Only show on rules tab */}
          {currentTab === 'rules' && (
            <Tooltip title="Open AI Rule Editor">
              <IconButton
                onClick={handleOpenAiEditor}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                  p: 1,
                  color: theme.palette.primary.main,
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <AIIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Tabs Navigation */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'transparent',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 'auto',
              px: 3,
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
          }}
        >
          <Tab value={TAB_CONFIG.encounters.value} label={TAB_CONFIG.encounters.label} />
          <Tab value={TAB_CONFIG.providers.value} label={TAB_CONFIG.providers.label} />
          <Tab value={TAB_CONFIG.rules.value} label={TAB_CONFIG.rules.label} />
          <Tab value={TAB_CONFIG.claims.value} label={TAB_CONFIG.claims.label} />
        </Tabs>
      </Paper>

      {/* Tab Content Container */}
      <Box>
        {/* Encounters Tab */}
        <TabPanel value="encounters" activeTab={currentTab}>
          <EncounterAuditsList filterByUserId={searchParams.get('provider') || undefined} />
        </TabPanel>

        {/* Clinicians Tab */}
        <TabPanel value="providers" activeTab={currentTab}>
          <ProvidersList />
        </TabPanel>

        {/* Rules Tab */}
        <TabPanel value="rules" activeTab={currentTab}>
          <RulesTabContent />
        </TabPanel>

        {/* Claims Tab placeholder */}
        <TabPanel value="claims" activeTab={currentTab}>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Claims monitoring coming soon</Typography>
          </Box>
        </TabPanel>
      </Box>
    </JotAuditPageLayout>
  );
};

export default JotAuditPage;
